import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import {
  Autocomplete,
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { Add, AddIcCall, CalendarMonth, DeleteOutline, PhoneCallback, PhoneInTalk } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { listAppointmentCalendars } from '../services/appointments';
import type { AppointmentCalendar } from '../types';
import { extractAgentInputDynamicVariableValues } from '../utils/agentDynamicVariables';
import { getApiErrorField, getApiErrorMessage } from '../utils/errors';
import {
  parseManualCallLaunchPreCallError,
  type ManualCallLaunchPreCallError,
} from '../utils/manualCallLaunchError';
import PhoneNumberField from './PhoneNumberField';
import WaveformPlayer from './WaveformPlayer';
import WaveformPlayerField from './WaveformPlayerField';

type LaunchMode = 'telephony_extension' | 'ai' | 'reverse_ivr';
type ReverseIvrDtmfStatus = 'converted' | 'optout' | 'mismatch';

export interface OutboundDialResult {
  callId?: string | null;
}

interface ReverseIvrDtmfRule {
  id: string;
  digit: string;
  status: ReverseIvrDtmfStatus;
  label: string;
}

interface SipTrunkPoolOption {
  id: string;
  name: string;
}

interface ScriptOption {
  id: string;
  name: string;
  eventType?: string;
}

interface TransferUserOption {
  id: string;
  name: string;
  email?: string;
}

interface TransferGroupOption {
  id: string;
  name: string;
}

interface TransferOptions {
  users?: TransferUserOption[];
  groups?: TransferGroupOption[];
}

interface TransferTargetAutocompleteOption {
  value: string;
  label: string;
}

interface ProviderOption {
  id: string;
  name: string;
}

interface AgentOption {
  id: string;
  name: string;
  transferTargetRequired?: boolean;
  appointmentCalendarRequired?: boolean;
  requiresPreCallScript?: boolean;
  config?: Record<string, unknown>;
}

interface ProviderResourcesEntry {
  agents?: AgentOption[];
}

interface ReverseIvrAudioOption {
  id: string;
  name: string;
  durationSeconds?: number | null;
  fileSize?: number | null;
  downloadUrl?: string | null;
  url?: string | null;
}

interface CallBootstrapCapability {
  enabled?: boolean;
  reasonCode?: string | null;
}

interface CallBootstrapPayload {
  capabilities?: {
    ai?: CallBootstrapCapability;
    reverseIvr?: CallBootstrapCapability;
    telephonyExtension?: CallBootstrapCapability;
  };
  flags?: {
    hasAnyMode?: boolean;
    hasScripts?: boolean;
    hasTransferTargets?: boolean;
  };
  resources?: {
    sipTrunkPools?: SipTrunkPoolOption[];
    scripts?: ScriptOption[];
    transferOptions?: TransferOptions;
    providers?: ProviderOption[];
    providerResources?: Record<string, ProviderResourcesEntry>;
    reverseIvrAudioOptions?: ReverseIvrAudioOption[];
  };
  limits?: {
    reverseIvr?: {
      ivrMaxAudioSeconds?: number;
      ivrMaxCallDurationSeconds?: number;
      ivrMaxPostAudioWaitSeconds?: number;
      ivrMaxAudioFileSizeBytes?: number;
    };
  };
  concurrency?: {
    hasSlot?: boolean;
    activeCalls?: number;
    effectiveLimit?: number | null;
    reasonCode?: string | null;
    modeHasSlot?: Partial<Record<LaunchMode, boolean | null>>;
    modeLimits?: Partial<Record<LaunchMode, number | null>>;
  };
}

export interface ManualCallLaunchPayload {
  mode: LaunchMode;
  toNumber: string;
  sipTrunkPoolId?: string;
  aiAgentProviderId?: string;
  agentId?: string;
  reverseIvrAudioFileId?: string;
  reverseIvrDtmfMap?: Record<string, { status: ReverseIvrDtmfStatus; label: string }>;
  dynamicVariables?: Record<string, string>;
  scriptIds?: string[];
  preCallScriptId?: string;
  appointmentCalendarId?: string;
  transferTarget?: {
    type: 'user' | 'group';
    id: string;
  };
}

interface GlobalTelephonyFabProps {
  visible: boolean;
  hasIncomingAlert: boolean;
  hasInProgressCall?: boolean;
  sipRegistering: boolean;
  outboundEndpointLabel?: string | null;
  loadCallBootstrap: () => Promise<CallBootstrapPayload>;
  onIncomingClick: () => void;
  onInProgressClick?: () => void;
  onManualLaunch: (payload: ManualCallLaunchPayload) => Promise<OutboundDialResult>;
}

interface FabPosition {
  x: number;
  y: number;
}

const FAB_SIZE_PX = 56;
const FAB_MARGIN_PX = 24;
const FAB_POSITION_STORAGE_KEY = 'contacttel-telephony-fab-position-v1';

const ringingPulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 45, 109, 0.72); }
  35% { transform: scale(1.28); box-shadow: 0 0 0 16px rgba(255, 45, 109, 0.24); }
  65% { transform: scale(0.9); box-shadow: 0 0 0 24px rgba(255, 45, 109, 0.08); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 45, 109, 0); }
`;

const ringingGlow = keyframes`
  0% { filter: brightness(1) saturate(1); }
  35% { filter: brightness(1.18) saturate(1.24); }
  65% { filter: brightness(0.95) saturate(1.08); }
  100% { filter: brightness(1) saturate(1); }
`;

const inProgressOuterHalo = keyframes`
  0% { opacity: 0.14; transform: scale(1); }
  45% { opacity: 0.36; transform: scale(1.34); }
  100% { opacity: 0.14; transform: scale(1); }
`;

function getDefaultPosition(): FabPosition {
  if (typeof window === 'undefined') {
    return { x: FAB_MARGIN_PX, y: 0 };
  }
  return {
    x: FAB_MARGIN_PX,
    y: Math.max(FAB_MARGIN_PX, window.innerHeight - FAB_SIZE_PX - FAB_MARGIN_PX),
  };
}

function clampPosition(position: FabPosition): FabPosition {
  if (typeof window === 'undefined') {
    return position;
  }
  const maxX = Math.max(FAB_MARGIN_PX, window.innerWidth - FAB_SIZE_PX - FAB_MARGIN_PX);
  const maxY = Math.max(FAB_MARGIN_PX, window.innerHeight - FAB_SIZE_PX - FAB_MARGIN_PX);
  return {
    x: Math.min(Math.max(position.x, FAB_MARGIN_PX), maxX),
    y: Math.min(Math.max(position.y, FAB_MARGIN_PX), maxY),
  };
}

function loadStoredPosition(): FabPosition {
  if (typeof window === 'undefined') {
    return getDefaultPosition();
  }
  const raw = window.localStorage.getItem(FAB_POSITION_STORAGE_KEY);
  if (!raw) {
    return getDefaultPosition();
  }
  try {
    const parsed = JSON.parse(raw) as Partial<FabPosition>;
    const x = Number(parsed.x);
    const y = Number(parsed.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      return getDefaultPosition();
    }
    return clampPosition({ x, y });
  } catch {
    return getDefaultPosition();
  }
}

function extractAgentDynamicVariables(agent: AgentOption | null): Record<string, string> {
  if (!agent || !agent.config || typeof agent.config !== 'object') {
    return {};
  }
  const config = agent.config as Record<string, unknown>;
  return extractAgentInputDynamicVariableValues(config.dynamicVariables);
}

function extractMediaPreviewUrl(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const data = payload as Record<string, unknown>;
  const previewCandidate = data.downloadUrl ?? data.download_url ?? data.url;
  if (typeof previewCandidate !== 'string') {
    return null;
  }
  const normalized = previewCandidate.trim();
  return normalized.length > 0 ? normalized : null;
}

const REVERSE_IVR_DTMF_STATUSES: ReverseIvrDtmfStatus[] = ['converted', 'optout', 'mismatch'];

function createReverseIvrDtmfRule(
  overrides?: Partial<Omit<ReverseIvrDtmfRule, 'id'>>,
): ReverseIvrDtmfRule {
  return {
    id: `ivr-dtmf-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    digit: '',
    status: 'mismatch',
    label: '',
    ...overrides,
  };
}

/**
 * Global telephony FAB available across authenticated routes.
 *
 * Features:
 * - movable position persisted in localStorage;
 * - incoming call highlight + looping ringtone;
 * - unified launcher modal for telephony, AI and reverse IVR manual calls.
 */
export default function GlobalTelephonyFab({
  visible,
  hasIncomingAlert,
  hasInProgressCall = false,
  sipRegistering,
  outboundEndpointLabel = null,
  loadCallBootstrap,
  onIncomingClick,
  onInProgressClick,
  onManualLaunch,
}: GlobalTelephonyFabProps) {
  const { t } = useTranslation(['common']);
  const [position, setPosition] = useState<FabPosition>(() => loadStoredPosition());
  const [dialOpen, setDialOpen] = useState(false);
  const [dialValue, setDialValue] = useState('');
  const [dialSubmitting, setDialSubmitting] = useState(false);
  const [dialError, setDialError] = useState<string | null>(null);
  const [dialPreCallError, setDialPreCallError] = useState<ManualCallLaunchPreCallError | null>(null);
  const [dialModeAlert, setDialModeAlert] = useState<string | null>(null);
  const [bootstrapLoading, setBootstrapLoading] = useState(false);
  const [bootstrapData, setBootstrapData] = useState<CallBootstrapPayload | null>(null);
  const [selectedMode, setSelectedMode] = useState<LaunchMode | ''>('');
  const [selectedSipTrunkPoolId, setSelectedSipTrunkPoolId] = useState('');
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [selectedReverseIvrAudioId, setSelectedReverseIvrAudioId] = useState('');
  const [reverseIvrAudioOptionsState, setReverseIvrAudioOptionsState] = useState<ReverseIvrAudioOption[]>([]);
  const [reverseIvrAudioPreviewById, setReverseIvrAudioPreviewById] = useState<Record<string, string>>({});
  const [reverseIvrAudioLookupLoading, setReverseIvrAudioLookupLoading] = useState(false);
  const [reverseIvrAudioUploading, setReverseIvrAudioUploading] = useState(false);
  const [reverseIvrAudioUploadProgress, setReverseIvrAudioUploadProgress] = useState<number | null>(null);
  const [reverseIvrAudioUploadError, setReverseIvrAudioUploadError] = useState<string | null>(null);
  const [reverseIvrUploadedAudioId, setReverseIvrUploadedAudioId] = useState<string | null>(null);
  const [reverseIvrUploadedPreviewUrl, setReverseIvrUploadedPreviewUrl] = useState<string | null>(null);
  const [reverseIvrUploadedFilename, setReverseIvrUploadedFilename] = useState<string | null>(null);
  const [reverseIvrNewAudioEnabled, setReverseIvrNewAudioEnabled] = useState(false);
  const [reverseIvrDtmfRules, setReverseIvrDtmfRules] = useState<ReverseIvrDtmfRule[]>([]);
  const [selectedScriptIds, setSelectedScriptIds] = useState<string[]>([]);
  const [selectedPreCallScriptId, setSelectedPreCallScriptId] = useState('');
  const [selectedTransferTarget, setSelectedTransferTarget] = useState('');
  const [appointmentCalendars, setAppointmentCalendars] = useState<AppointmentCalendar[]>([]);
  const [appointmentCalendarsLoading, setAppointmentCalendarsLoading] = useState(false);
  const [appointmentCalendarsError, setAppointmentCalendarsError] = useState<string | null>(null);
  const [selectedAppointmentCalendarId, setSelectedAppointmentCalendarId] = useState('');
  const [agentVariableValues, setAgentVariableValues] = useState<Record<string, string>>({});

  const dragStateRef = useRef<{ dragging: boolean; offsetX: number; offsetY: number }>({
    dragging: false,
    offsetX: 0,
    offsetY: 0,
  });
  const movedSinceMouseDownRef = useRef(false);
  const ringtoneAudioRef = useRef<HTMLAudioElement | null>(null);
  const ringtoneUnlockedRef = useRef(false);
  const pendingRingtoneRetryRef = useRef(false);
  const shouldRingRef = useRef(false);
  const ringtonePlayTokenRef = useRef(0);
  const uploadedReverseIvrPreviewRef = useRef<string | null>(null);

  const tooltipLabel = useMemo(() => {
    if (hasIncomingAlert) {
      return t('common:telephonyFab.openIncoming', 'Abrir chamada recebida');
    }
    if (hasInProgressCall) {
      return t('common:telephonyFab.callInProgress', 'Chamada em andamento');
    }
    if (sipRegistering) {
      return t('common:telephonyFab.registration.registering', 'Registrando ramal...');
    }
    return t('common:telephonyFab.newCall', 'Nova ligação');
  }, [hasIncomingAlert, hasInProgressCall, sipRegistering, t]);

  const resolveModeUnavailableMessage = useCallback((reasonCode?: string | null): string => {
    const normalized = String(reasonCode || '').trim();
    if (!normalized) {
      return t(
        'common:telephonyFab.dial.modeUnavailable',
        'A modalidade de ligação selecionada não está disponível para este usuário.',
      );
    }
    switch (normalized) {
      case 'tenant_inactive':
        return t(
          'common:telephonyFab.dial.unavailableReasons.tenantInactive',
          'O tenant está inativo para iniciar ligações.',
        );
      case 'subscription_inactive_or_missing':
        return t(
          'common:telephonyFab.dial.unavailableReasons.subscriptionRequired',
          'É necessária uma assinatura ativa para iniciar ligações.',
        );
      case 'ai_balance_unavailable':
        return t(
          'common:telephonyFab.dial.unavailableReasons.insufficientAiMinutes',
          'Sem minutos de IA disponíveis.',
        );
      case 'telephony_overage_pending':
        return t(
          'common:telephonyFab.dial.unavailableReasons.overagePending',
          'Existe overage pendente de telefonia. Regularize para continuar.',
        );
      case 'telephony_balance_unavailable':
        return t(
          'common:telephonyFab.dial.unavailableReasons.insufficientMinutes',
          'Sem minutos de telefonia disponíveis.',
        );
      case 'endpoint_missing_or_inactive':
        return t(
          'common:telephonyFab.dial.unavailableReasons.endpointMissing',
          'Nenhum ramal SIP ativo foi encontrado para o usuário.',
        );
      case 'endpoint_type_not_supported':
        return t(
          'common:telephonyFab.dial.unavailableReasons.endpointUnsupported',
          'O tipo de ramal configurado não é suportado para esta ação.',
        );
      case 'endpoint_extension_missing':
        return t(
          'common:telephonyFab.dial.unavailableReasons.extensionMissing',
          'O ramal configurado não possui extensão válida.',
        );
      case 'operator_unavailable':
        return t(
          'common:telephonyFab.dial.unavailableReasons.operatorUnavailable',
          'O operador está indisponível para novas ligações de ramal.',
        );
      case 'sip_contact_unavailable':
        return t(
          'common:telephonyFab.dial.unavailableReasons.sipContactUnavailable',
          'O ramal SIP não está acessível no momento. Confira a conexão e tente novamente.',
        );
      case 'sip_trunk_missing':
      case 'sip_trunk_pool_missing':
      case 'no_active_sip_trunk_available':
      case 'no_active_sip_trunk_pool_available':
        return t(
          'common:telephonyFab.dial.noSipTrunkPools',
          'Nenhum pool de saída ativo disponível.',
        );
      case 'reverse_ivr_audio_missing':
        return t(
          'common:telephonyFab.dial.unavailableReasons.reverseIvrAudioMissing',
          'Nenhum áudio de URA Reversa preparado foi encontrado na biblioteca.',
        );
      case 'ai_agent_endpoint_missing':
        return t(
          'common:telephonyFab.dial.unavailableReasons.aiAgentEndpointMissing',
          'Nenhum endpoint ativo foi encontrado para os agentes disponíveis.',
        );
      case 'tenant_concurrency_exhausted':
        return t(
          'common:telephonyFab.dial.unavailableReasons.tenantConcurrencyExhausted',
          'Sem slot de concorrência disponível para este tenant no momento.',
        );
      case 'permission_denied':
        return t(
          'common:telephonyFab.dial.unavailableReasons.permissionDenied',
          'Você não tem permissão para iniciar essa modalidade de ligação.',
        );
      default:
        return t(
          'common:telephonyFab.dial.modeUnavailable',
          'A modalidade de ligação selecionada não está disponível para este usuário.',
        );
    }
  }, [t]);

  const stopRingtone = useCallback(() => {
    const audio = ringtoneAudioRef.current;
    pendingRingtoneRetryRef.current = false;
    if (!audio) return;
    ringtonePlayTokenRef.current += 1;
    audio.pause();
    audio.currentTime = 0;
  }, []);

  const playRingtoneIfNeeded = useCallback(async () => {
    if (!shouldRingRef.current) {
      pendingRingtoneRetryRef.current = false;
      return;
    }

    if (!ringtoneAudioRef.current) {
      ringtoneAudioRef.current = new Audio('/audio/ringtone.mp3');
      ringtoneAudioRef.current.loop = true;
    }

    const audio = ringtoneAudioRef.current;
    const token = ringtonePlayTokenRef.current + 1;
    ringtonePlayTokenRef.current = token;

    try {
      await audio.play();
      if (!shouldRingRef.current || token !== ringtonePlayTokenRef.current) {
        audio.pause();
        audio.currentTime = 0;
      }
    } catch (error) {
      if (!shouldRingRef.current || token !== ringtonePlayTokenRef.current) {
        return;
      }
      pendingRingtoneRetryRef.current = true;
      console.info('[GlobalTelephonyFab] ringtone play blocked by browser policy', error);
    }
  }, []);

  const tryUnlockRingtone = useCallback(async () => {
    const audio = ringtoneAudioRef.current;
    if (!audio || ringtoneUnlockedRef.current) {
      return;
    }
    try {
      await audio.play();
      audio.pause();
      audio.currentTime = 0;
      ringtoneUnlockedRef.current = true;
      if (pendingRingtoneRetryRef.current && shouldRingRef.current) {
        pendingRingtoneRetryRef.current = false;
        await playRingtoneIfNeeded();
      }
    } catch (error) {
      console.info('[GlobalTelephonyFab] ringtone unlock deferred by browser', error);
    }
  }, [playRingtoneIfNeeded]);

  useEffect(() => {
    shouldRingRef.current = visible && hasIncomingAlert;

    if (!visible || !hasIncomingAlert) {
      stopRingtone();
      return;
    }

    playRingtoneIfNeeded().catch(() => undefined);
    return () => stopRingtone();
  }, [hasIncomingAlert, playRingtoneIfNeeded, stopRingtone, visible]);

  useEffect(() => {
    if (!visible) return undefined;

    const handleUserGesture = () => {
      tryUnlockRingtone().catch(() => undefined);
    };

    window.addEventListener('pointerdown', handleUserGesture, { passive: true });
    window.addEventListener('keydown', handleUserGesture);
    return () => {
      window.removeEventListener('pointerdown', handleUserGesture);
      window.removeEventListener('keydown', handleUserGesture);
    };
  }, [tryUnlockRingtone, visible]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(FAB_POSITION_STORAGE_KEY, JSON.stringify(position));
  }, [position]);

  const releaseUploadedReverseIvrPreview = useCallback(() => {
    const currentPreviewUrl = uploadedReverseIvrPreviewRef.current;
    if (currentPreviewUrl && currentPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(currentPreviewUrl);
    }
    uploadedReverseIvrPreviewRef.current = null;
  }, []);

  useEffect(() => (
    () => {
      releaseUploadedReverseIvrPreview();
    }
  ), [releaseUploadedReverseIvrPreview]);

  const resetLauncherState = useCallback(() => {
    releaseUploadedReverseIvrPreview();
    setDialSubmitting(false);
    setDialError(null);
    setDialPreCallError(null);
    setDialModeAlert(null);
    setBootstrapLoading(false);
    setBootstrapData(null);
    setSelectedMode('');
    setSelectedSipTrunkPoolId('');
    setSelectedProviderId('');
    setSelectedAgentId('');
    setSelectedPreCallScriptId('');
    setSelectedReverseIvrAudioId('');
    setReverseIvrAudioOptionsState([]);
    setReverseIvrAudioPreviewById({});
    setReverseIvrAudioLookupLoading(false);
    setReverseIvrAudioUploading(false);
    setReverseIvrAudioUploadProgress(null);
    setReverseIvrAudioUploadError(null);
    setReverseIvrUploadedAudioId(null);
    setReverseIvrUploadedPreviewUrl(null);
    setReverseIvrUploadedFilename(null);
    setReverseIvrNewAudioEnabled(false);
    setReverseIvrDtmfRules([]);
    setSelectedScriptIds([]);
    setSelectedTransferTarget('');
    setAppointmentCalendars([]);
    setAppointmentCalendarsLoading(false);
    setAppointmentCalendarsError(null);
    setSelectedAppointmentCalendarId('');
    setAgentVariableValues({});
  }, [releaseUploadedReverseIvrPreview]);

  useEffect(() => {
    if (!visible) {
      setDialOpen(false);
      resetLauncherState();
      stopRingtone();
    }
  }, [resetLauncherState, stopRingtone, visible]);

  const availableModeEntries = useMemo(() => {
    const capabilities = bootstrapData?.capabilities;
    return [
      { mode: 'telephony_extension' as const, capability: capabilities?.telephonyExtension ?? null },
      { mode: 'ai' as const, capability: capabilities?.ai ?? null },
      { mode: 'reverse_ivr' as const, capability: capabilities?.reverseIvr ?? null },
    ];
  }, [bootstrapData]);

  const enabledModes = useMemo(
    () => availableModeEntries.filter((entry) => Boolean(entry.capability?.enabled)).map((entry) => entry.mode),
    [availableModeEntries],
  );

  useEffect(() => {
    if (!dialOpen) {
      return;
    }
    resetLauncherState();
    setBootstrapLoading(true);

    let isMounted = true;
    loadCallBootstrap()
      .then((bootstrap) => {
        if (!isMounted) return;
        setBootstrapData(bootstrap);
        const reverseAudioOptions = Array.isArray(bootstrap?.resources?.reverseIvrAudioOptions)
          ? bootstrap.resources.reverseIvrAudioOptions
          : [];
        setReverseIvrAudioOptionsState(reverseAudioOptions);
        setReverseIvrAudioPreviewById(
          reverseAudioOptions.reduce<Record<string, string>>((acc, option) => {
            const previewUrl = extractMediaPreviewUrl(option);
            if (previewUrl) {
              acc[option.id] = previewUrl;
            }
            return acc;
          }, {}),
        );

        const tenantHasConcurrencySlot = bootstrap?.concurrency?.hasSlot ?? true;
        if (!tenantHasConcurrencySlot) {
          setSelectedMode('');
          setDialModeAlert(
            t(
              'common:telephonyFab.dial.concurrencyUnavailable',
              'No momento não há slot de concorrência disponível para este tenant. Tente novamente em alguns minutos.',
            ),
          );
          return;
        }

        const hasAnyMode = Boolean(bootstrap?.flags?.hasAnyMode);
        const modeEntries = [
          { mode: 'telephony_extension' as const, capability: bootstrap?.capabilities?.telephonyExtension ?? null },
          { mode: 'ai' as const, capability: bootstrap?.capabilities?.ai ?? null },
          { mode: 'reverse_ivr' as const, capability: bootstrap?.capabilities?.reverseIvr ?? null },
        ];
        const enabled = modeEntries.filter((entry) => Boolean(entry.capability?.enabled)).map((entry) => entry.mode);
        if (!hasAnyMode || enabled.length === 0) {
          setDialModeAlert(
            t(
              'common:telephonyFab.dial.noModesAvailable',
              'Nenhuma modalidade de ligação está disponível para este usuário neste momento.',
            ),
          );
          return;
        }

        const firstMode = enabled[0];
        setSelectedMode(firstMode);

        const sipTrunkPools = bootstrap?.resources?.sipTrunkPools || [];
        if (sipTrunkPools.length === 1) {
          setSelectedSipTrunkPoolId(sipTrunkPools[0].id);
        }
      })
      .catch(() => {
        if (!isMounted) return;
        setDialModeAlert(
          t(
            'common:telephonyFab.dial.bootstrapLoadError',
            'Não foi possível carregar as modalidades de ligação disponíveis.',
          ),
        );
      })
      .finally(() => {
        if (!isMounted) return;
        setBootstrapLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [dialOpen, loadCallBootstrap, resetLauncherState, t]);

  useEffect(() => {
    if (!dialOpen) {
      return;
    }

    let isMounted = true;
    setAppointmentCalendarsLoading(true);
    setAppointmentCalendarsError(null);
    listAppointmentCalendars({
      page: 1,
      pageSize: 200,
      isActive: true,
      ordering: 'name',
    })
      .then((payload) => {
        if (!isMounted) return;
        setAppointmentCalendars(Array.isArray(payload.results) ? payload.results : []);
      })
      .catch((requestError) => {
        if (!isMounted) return;
        console.error('Error loading appointment calendars:', requestError);
        setAppointmentCalendars([]);
        setAppointmentCalendarsError(
          t('common:telephonyFab.launcher.appointmentCalendarLoadError'),
        );
      })
      .finally(() => {
        if (isMounted) {
          setAppointmentCalendarsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [dialOpen, t]);

  useEffect(() => {
    if (!selectedMode || !bootstrapData) {
      return;
    }
    setDialError(null);
    setDialPreCallError(null);
    setReverseIvrAudioUploadError(null);
    setReverseIvrAudioLookupLoading(false);
    setSelectedProviderId('');
    setSelectedAgentId('');
    setSelectedPreCallScriptId('');
    setSelectedAppointmentCalendarId('');
    setSelectedReverseIvrAudioId('');
    setAgentVariableValues({});
    setReverseIvrUploadedAudioId(null);
    setReverseIvrUploadedPreviewUrl(null);
    setReverseIvrUploadedFilename(null);
    setReverseIvrNewAudioEnabled(false);
    setReverseIvrDtmfRules([]);
    releaseUploadedReverseIvrPreview();
    if (selectedMode === 'telephony_extension' || selectedMode === 'reverse_ivr') {
      const sipTrunkPools = bootstrapData?.resources?.sipTrunkPools || [];
      if (sipTrunkPools.length === 1) {
        setSelectedSipTrunkPoolId(sipTrunkPools[0].id);
      }
    }
    if (selectedMode === 'reverse_ivr') {
      const audios = bootstrapData?.resources?.reverseIvrAudioOptions || [];
      if (audios.length === 1) {
        setSelectedReverseIvrAudioId(audios[0].id);
      }
    }
    if (selectedMode === 'ai') {
      const providers = bootstrapData?.resources?.providers || [];
      if (providers.length === 1) {
        setSelectedProviderId(providers[0].id);
      }
    }
  }, [bootstrapData, releaseUploadedReverseIvrPreview, selectedMode]);

  const providerResources = useMemo(() => {
    if (!selectedProviderId) {
      return null;
    }
    return bootstrapData?.resources?.providerResources?.[selectedProviderId] ?? null;
  }, [bootstrapData, selectedProviderId]);

  const providerAgents = useMemo(
    () => providerResources?.agents || [],
    [providerResources],
  );
  const selectedAgent = useMemo(
    () => providerAgents.find((agent) => agent.id === selectedAgentId) || null,
    [providerAgents, selectedAgentId],
  );
  const selectedAppointmentCalendar = useMemo(
    () => appointmentCalendars.find((calendar) => calendar.id === selectedAppointmentCalendarId) || null,
    [appointmentCalendars, selectedAppointmentCalendarId],
  );
  const isAppointmentCalendarRequired = selectedMode === 'ai' && Boolean(selectedAgent?.appointmentCalendarRequired);

  useEffect(() => {
    if (selectedMode !== 'ai') {
      return;
    }
    if (providerAgents.length === 1 && !selectedAgentId) {
      setSelectedAgentId(providerAgents[0].id);
    }
  }, [providerAgents, selectedAgentId, selectedMode]);

  useEffect(() => {
    if (selectedMode !== 'ai') {
      return;
    }
    setAgentVariableValues(extractAgentDynamicVariables(selectedAgent));
  }, [selectedAgent, selectedMode]);

  const scripts = useMemo(
    () => bootstrapData?.resources?.scripts || [],
    [bootstrapData],
  );
  const preCallScripts = useMemo(
    () => scripts.filter((script) => script.eventType === 'pre_call'),
    [scripts],
  );
  const callScripts = useMemo(
    () => scripts.filter((script) => script.eventType !== 'pre_call'),
    [scripts],
  );
  const transferUsers = useMemo(
    () => bootstrapData?.resources?.transferOptions?.users || [],
    [bootstrapData],
  );
  const transferGroups = useMemo(
    () => bootstrapData?.resources?.transferOptions?.groups || [],
    [bootstrapData],
  );
  const reverseIvrAudioOptions = reverseIvrAudioOptionsState;
  const ivrMaxAudioSeconds = bootstrapData?.limits?.reverseIvr?.ivrMaxAudioSeconds || null;
  const ivrMaxAudioFileSizeBytes = bootstrapData?.limits?.reverseIvr?.ivrMaxAudioFileSizeBytes || null;
  const hasTenantConcurrencySlot = bootstrapData?.concurrency?.hasSlot ?? true;

  const selectedReverseIvrAudioOption = useMemo(
    () => reverseIvrAudioOptions.find((item) => item.id === selectedReverseIvrAudioId) || null,
    [reverseIvrAudioOptions, selectedReverseIvrAudioId],
  );

  const selectedReverseIvrAudioPreviewUrl = useMemo(() => {
    if (!selectedReverseIvrAudioId) {
      return null;
    }
    if (selectedReverseIvrAudioId === reverseIvrUploadedAudioId && reverseIvrUploadedPreviewUrl) {
      return reverseIvrUploadedPreviewUrl;
    }
    return (
      reverseIvrAudioPreviewById[selectedReverseIvrAudioId]
      || extractMediaPreviewUrl(selectedReverseIvrAudioOption)
      || null
    );
  }, [
    reverseIvrAudioPreviewById,
    reverseIvrUploadedAudioId,
    reverseIvrUploadedPreviewUrl,
    selectedReverseIvrAudioId,
    selectedReverseIvrAudioOption,
  ]);

  const reverseIvrAudioFieldConstraints = useMemo(
    () => ({
      maxDurationSeconds: ivrMaxAudioSeconds || undefined,
      maxFileSizeBytes: ivrMaxAudioFileSizeBytes || undefined,
    }),
    [ivrMaxAudioFileSizeBytes, ivrMaxAudioSeconds],
  );

  const hasAgentDynamicVariables = Object.keys(agentVariableValues).length > 0;
  const selectedAgentVariableEntries = Object.entries(agentVariableValues);
  const showUploadedReverseIvrWaveform = Boolean(
    reverseIvrUploadedAudioId
    && selectedReverseIvrAudioId === reverseIvrUploadedAudioId
    && selectedReverseIvrAudioPreviewUrl,
  );
  const selectedScriptOptions = useMemo(
    () => callScripts.filter((item) => selectedScriptIds.includes(item.id)),
    [callScripts, selectedScriptIds],
  );
  const selectedPreCallScriptOption = useMemo(
    () => preCallScripts.find((item) => item.id === selectedPreCallScriptId) || null,
    [preCallScripts, selectedPreCallScriptId],
  );
  const transferTargetOptions = useMemo<TransferTargetAutocompleteOption[]>(
    () => ([
      ...transferUsers.map((user) => ({
        value: `user:${user.id}`,
        label: t('common:telephonyFab.launcher.transferTargetUser', {
          defaultValue: 'Usuário: {{name}}',
          name: user.name,
        }),
      })),
      ...transferGroups.map((group) => ({
        value: `group:${group.id}`,
        label: t('common:telephonyFab.launcher.transferTargetGroup', {
          defaultValue: 'Grupo: {{name}}',
          name: group.name,
        }),
      })),
    ]),
    [t, transferGroups, transferUsers],
  );
  const selectedTransferTargetOption = useMemo(
    () => transferTargetOptions.find((item) => item.value === selectedTransferTarget) || null,
    [selectedTransferTarget, transferTargetOptions],
  );
  const canUseScripts = selectedMode === 'ai' || selectedMode === 'reverse_ivr';
  const canUseTransferTarget = selectedMode === 'ai';
  const isTransferTargetRequired = selectedMode === 'ai' && Boolean(selectedAgent?.transferTargetRequired);
  const isPreCallScriptRequired = selectedMode === 'ai' && Boolean(selectedAgent?.requiresPreCallScript);

  const reverseIvrDtmfValidationError = useMemo<'missing_digit' | 'duplicate_digit' | null>(() => {
    if (selectedMode !== 'reverse_ivr' || reverseIvrDtmfRules.length === 0) {
      return null;
    }
    const seenDigits = new Set<string>();
    for (const rule of reverseIvrDtmfRules) {
      const digit = String(rule.digit || '').trim();
      if (!digit) {
        return 'missing_digit';
      }
      if (seenDigits.has(digit)) {
        return 'duplicate_digit';
      }
      seenDigits.add(digit);
    }
    return null;
  }, [reverseIvrDtmfRules, selectedMode]);

  const reverseIvrDtmfMapPayload = useMemo(() => {
    const map: Record<string, { status: ReverseIvrDtmfStatus; label: string }> = {};
    reverseIvrDtmfRules.forEach((rule) => {
      const digit = String(rule.digit || '').trim();
      if (!digit || map[digit]) {
        return;
      }
      map[digit] = {
        status: rule.status,
        label: String(rule.label || '').trim(),
      };
    });
    return map;
  }, [reverseIvrDtmfRules]);

  useEffect(() => {
    if (!canUseScripts && selectedScriptIds.length > 0) {
      setSelectedScriptIds([]);
    }
    if (selectedMode !== 'ai' && selectedPreCallScriptId) {
      setSelectedPreCallScriptId('');
    } else if (
      selectedPreCallScriptId
      && !preCallScripts.some((script) => script.id === selectedPreCallScriptId)
    ) {
      setSelectedPreCallScriptId('');
    }
    if (isPreCallScriptRequired && preCallScripts.length === 1 && !selectedPreCallScriptId) {
      setSelectedPreCallScriptId(preCallScripts[0].id);
    }
    if ((!canUseTransferTarget || !isTransferTargetRequired) && selectedTransferTarget) {
      setSelectedTransferTarget('');
    }
    if (!isAppointmentCalendarRequired && selectedAppointmentCalendarId) {
      setSelectedAppointmentCalendarId('');
      return;
    }
    if (isAppointmentCalendarRequired && appointmentCalendars.length === 1 && !selectedAppointmentCalendarId) {
      setSelectedAppointmentCalendarId(appointmentCalendars[0].id);
      return;
    }
    if (
      isAppointmentCalendarRequired
      && selectedAppointmentCalendarId
      && !appointmentCalendars.some((calendar) => calendar.id === selectedAppointmentCalendarId)
    ) {
      setSelectedAppointmentCalendarId('');
    }
  }, [
    appointmentCalendars,
    canUseScripts,
    canUseTransferTarget,
    isAppointmentCalendarRequired,
    isTransferTargetRequired,
    isPreCallScriptRequired,
    preCallScripts,
    selectedAppointmentCalendarId,
    selectedMode,
    selectedPreCallScriptId,
    selectedScriptIds,
    selectedTransferTarget,
  ]);

  const isDialFormReady = useMemo(() => {
    if (
      bootstrapLoading
      || reverseIvrAudioUploading
      || Boolean(dialModeAlert)
      || !selectedMode
      || !hasTenantConcurrencySlot
      || hasInProgressCall
    ) {
      return false;
    }

    const destination = dialValue.trim();
    if (!destination) {
      return false;
    }

    const sipTrunkPools = bootstrapData?.resources?.sipTrunkPools || [];
    const hasSipTrunkPool = Boolean(selectedSipTrunkPoolId || sipTrunkPools.length === 1);

    if (selectedMode === 'telephony_extension') {
      return hasSipTrunkPool;
    }
    if (selectedMode === 'ai') {
      if (!(selectedProviderId && selectedAgentId)) {
        return false;
      }
      if (isPreCallScriptRequired && !selectedPreCallScriptId) {
        return false;
      }
      if (isAppointmentCalendarRequired && !selectedAppointmentCalendarId) {
        return false;
      }
      if (!isTransferTargetRequired) {
        return true;
      }
      const [targetType, targetId] = String(selectedTransferTarget || '').trim().split(':');
      return Boolean((targetType === 'user' || targetType === 'group') && targetId);
    }
    if (reverseIvrDtmfValidationError) {
      return false;
    }
    if (!hasSipTrunkPool) {
      return false;
    }
    if (reverseIvrNewAudioEnabled) {
      return Boolean(
        reverseIvrUploadedAudioId
        && selectedReverseIvrAudioId
        && selectedReverseIvrAudioId === reverseIvrUploadedAudioId,
      );
    }
    return Boolean(selectedReverseIvrAudioId);
  }, [
    bootstrapData,
    bootstrapLoading,
    dialModeAlert,
    dialValue,
    hasInProgressCall,
    hasTenantConcurrencySlot,
    reverseIvrAudioUploading,
    reverseIvrDtmfValidationError,
    reverseIvrNewAudioEnabled,
    reverseIvrUploadedAudioId,
    isAppointmentCalendarRequired,
    isTransferTargetRequired,
    isPreCallScriptRequired,
    selectedAppointmentCalendarId,
    selectedAgentId,
    selectedMode,
    selectedProviderId,
    selectedPreCallScriptId,
    selectedReverseIvrAudioId,
    selectedTransferTarget,
    selectedSipTrunkPoolId,
  ]);

  useEffect(() => {
    if (selectedMode !== 'reverse_ivr') {
      return;
    }
    const targetAudioId = String(selectedReverseIvrAudioId || '').trim();
    if (!targetAudioId || targetAudioId === reverseIvrUploadedAudioId || reverseIvrAudioPreviewById[targetAudioId]) {
      return;
    }
    let isMounted = true;
    setReverseIvrAudioLookupLoading(true);
    api.get(`/api/media/${targetAudioId}/`)
      .then((response) => {
        if (!isMounted) {
          return;
        }
        const previewUrl = extractMediaPreviewUrl(response?.data);
        if (!previewUrl) {
          return;
        }
        setReverseIvrAudioPreviewById((prev) => ({ ...prev, [targetAudioId]: previewUrl }));
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }
        console.error('[GlobalTelephonyFab] failed to load reverse IVR audio preview', error);
      })
      .finally(() => {
        if (!isMounted) {
          return;
        }
        setReverseIvrAudioLookupLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [reverseIvrAudioPreviewById, reverseIvrUploadedAudioId, selectedMode, selectedReverseIvrAudioId]);

  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => clampPosition(prev));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!dragStateRef.current.dragging) return;
      const nextPosition = clampPosition({
        x: event.clientX - dragStateRef.current.offsetX,
        y: event.clientY - dragStateRef.current.offsetY,
      });
      if (
        Math.abs(nextPosition.x - position.x) > 2
        || Math.abs(nextPosition.y - position.y) > 2
      ) {
        movedSinceMouseDownRef.current = true;
      }
      setPosition(nextPosition);
    };

    const handleMouseUp = () => {
      dragStateRef.current.dragging = false;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [position.x, position.y]);

  const handleMouseDown = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    movedSinceMouseDownRef.current = false;
    dragStateRef.current = {
      dragging: true,
      offsetX: event.clientX - position.x,
      offsetY: event.clientY - position.y,
    };
  };

  const handleFabClick = () => {
    tryUnlockRingtone().catch(() => undefined);

    if (movedSinceMouseDownRef.current) {
      movedSinceMouseDownRef.current = false;
      return;
    }

    if (hasIncomingAlert) {
      stopRingtone();
      onIncomingClick();
      return;
    }

    setDialError(null);
    setDialPreCallError(null);
    setDialModeAlert(null);
    setDialOpen(true);
  };

  const isFabDisabled = false;
  const shouldAnimateInProgress = hasInProgressCall && !hasIncomingAlert;

  const modeCapability = useMemo(() => {
    if (!selectedMode || !bootstrapData?.capabilities) {
      return null;
    }
    if (selectedMode === 'telephony_extension') {
      return bootstrapData.capabilities.telephonyExtension ?? null;
    }
    if (selectedMode === 'reverse_ivr') {
      return bootstrapData.capabilities.reverseIvr ?? null;
    }
    return bootstrapData.capabilities.ai ?? null;
  }, [bootstrapData, selectedMode]);

  const buildTransferTargetPayload = (): { type: 'user' | 'group'; id: string } | undefined => {
    const raw = String(selectedTransferTarget || '').trim();
    if (!raw) return undefined;
    const [targetType, targetId] = raw.split(':');
    if ((targetType !== 'user' && targetType !== 'group') || !targetId) {
      return undefined;
    }
    return { type: targetType, id: targetId };
  };

  /**
   * Uploads a new Reverse IVR audio file and selects it immediately.
   * Keeps local blob preview as fallback until backend download URL is available.
   */
  const handleReverseIvrAudioUpload = useCallback(async (file: File) => {
    setDialError(null);
    setReverseIvrAudioUploadError(null);
    setReverseIvrAudioLookupLoading(false);
    setReverseIvrAudioUploading(true);
    setReverseIvrAudioUploadProgress(null);

    releaseUploadedReverseIvrPreview();
    const localPreviewUrl = URL.createObjectURL(file);
    uploadedReverseIvrPreviewRef.current = localPreviewUrl;
    setReverseIvrUploadedPreviewUrl(localPreviewUrl);
    setReverseIvrUploadedFilename(file.name);

    const uploadPayload = new FormData();
    uploadPayload.append('file', file);
    uploadPayload.append('file_type', 'reverse_ivr_audio');
    uploadPayload.append(
      'metadata',
      JSON.stringify({
        source: 'manual_call_launcher',
        mode: 'reverse_ivr',
      }),
    );

    try {
      const uploadResponse = await api.post('/api/media/upload/', uploadPayload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total || progressEvent.total <= 0) {
            setReverseIvrAudioUploadProgress(null);
            return;
          }
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setReverseIvrAudioUploadProgress(percentCompleted);
        },
      });
      const uploadedAudioId = String(uploadResponse?.data?.id || '').trim();
      if (!uploadedAudioId) {
        throw new Error('media_upload_without_id');
      }

      const uploadedAudioName = String(
        uploadResponse?.data?.originalFilename
        || uploadResponse?.data?.original_filename
        || file.name,
      );
      const uploadedPreviewUrl = extractMediaPreviewUrl(uploadResponse?.data) || localPreviewUrl;

      setReverseIvrAudioOptionsState((prev) => {
        const deduped = prev.filter((item) => item.id !== uploadedAudioId);
        return [
          {
            id: uploadedAudioId,
            name: uploadedAudioName,
            fileSize: file.size,
            downloadUrl: uploadedPreviewUrl,
          },
          ...deduped,
        ];
      });
      setReverseIvrAudioPreviewById((prev) => ({ ...prev, [uploadedAudioId]: uploadedPreviewUrl }));
      setSelectedReverseIvrAudioId(uploadedAudioId);
      setReverseIvrUploadedAudioId(uploadedAudioId);
      setReverseIvrUploadedFilename(uploadedAudioName);
      setReverseIvrUploadedPreviewUrl(uploadedPreviewUrl);
      if (uploadedPreviewUrl !== localPreviewUrl) {
        releaseUploadedReverseIvrPreview();
      }
    } catch (error) {
      console.error('[GlobalTelephonyFab] failed to upload reverse IVR audio', error);
      setReverseIvrAudioUploadError(
        t(
          'common:telephonyFab.launcher.reverseIvrAudioUploadFailed',
          'Não foi possível enviar o áudio da URA Reversa.',
        ),
      );
    } finally {
      setReverseIvrAudioUploading(false);
      setReverseIvrAudioUploadProgress(null);
    }
  }, [releaseUploadedReverseIvrPreview, t]);

  const handleClearReverseIvrUploadSelection = useCallback(() => {
    releaseUploadedReverseIvrPreview();
    setReverseIvrUploadedAudioId(null);
    setReverseIvrUploadedPreviewUrl(null);
    setReverseIvrUploadedFilename(null);
    setSelectedReverseIvrAudioId('');
    setReverseIvrAudioUploadError(null);
  }, [releaseUploadedReverseIvrPreview]);

  const handleDialSubmit = async () => {
    if (bootstrapLoading) {
      return;
    }
    if (!hasTenantConcurrencySlot) {
      setDialError(
        t(
          'common:telephonyFab.dial.concurrencyUnavailable',
          'No momento não há slot de concorrência disponível para este tenant. Tente novamente em alguns minutos.',
        ),
      );
      return;
    }
    if (hasInProgressCall) {
      setDialError(
        t(
          'common:telephonyFab.dial.extensionBusy',
          'Seu ramal está ocupado em outra ligação. Abra a chamada em andamento para continuar.',
        ),
      );
      return;
    }
    if (reverseIvrAudioUploading) {
      setDialError(
        t(
          'common:telephonyFab.launcher.reverseIvrAudioUploadInProgress',
          'Aguarde o upload do áudio da URA Reversa terminar para iniciar a ligação.',
        ),
      );
      return;
    }
    if (!selectedMode) {
      setDialError(t('common:telephonyFab.dial.modeRequired', 'Selecione uma modalidade de ligação.'));
      return;
    }

    if (!modeCapability?.enabled) {
      setDialError(resolveModeUnavailableMessage(modeCapability?.reasonCode));
      return;
    }

    const destination = dialValue.trim();
    if (!destination) {
      setDialError(t('common:telephonyFab.dial.required', 'Informe um número para discar.'));
      return;
    }
    if (selectedMode === 'reverse_ivr' && reverseIvrDtmfValidationError) {
      setDialError(
        reverseIvrDtmfValidationError === 'duplicate_digit'
          ? t(
            'common:telephonyFab.launcher.reverseIvrDtmfDuplicateDigit',
            'Não é permitido repetir o mesmo dígito no mapeamento DTMF.',
          )
          : t(
            'common:telephonyFab.launcher.reverseIvrDtmfMissingDigit',
            'Selecione um dígito para todas as regras de mapeamento DTMF.',
          ),
      );
      return;
    }

    const payload: ManualCallLaunchPayload = {
      mode: selectedMode,
      toNumber: destination,
      dynamicVariables: selectedMode === 'ai' ? agentVariableValues : undefined,
    };
    if (canUseScripts) {
      payload.scriptIds = selectedScriptIds;
    }
    if (selectedMode === 'ai' && selectedPreCallScriptId) {
      payload.preCallScriptId = selectedPreCallScriptId;
    }
    if (selectedMode === 'ai' && isPreCallScriptRequired && !selectedPreCallScriptId) {
      setDialError(t('common:telephonyFab.launcher.preCallScriptRequired'));
      return;
    }
    if (canUseTransferTarget && isTransferTargetRequired) {
      const transferTargetPayload = buildTransferTargetPayload();
      if (!transferTargetPayload) {
        setDialError(
          t(
            'common:telephonyFab.launcher.transferTargetRequired',
            'Selecione um destino de transferência para este agente.',
          ),
        );
        return;
      }
      payload.transferTarget = transferTargetPayload;
    }
    if (selectedMode === 'ai' && isAppointmentCalendarRequired) {
      if (!selectedAppointmentCalendarId) {
        setDialError(
          t('common:telephonyFab.launcher.appointmentCalendarRequired'),
        );
        return;
      }
      payload.appointmentCalendarId = selectedAppointmentCalendarId;
    }

    if (selectedMode === 'telephony_extension') {
      const sipTrunkPools = bootstrapData?.resources?.sipTrunkPools || [];
      const effectiveSipTrunkPoolId = selectedSipTrunkPoolId || (sipTrunkPools.length === 1 ? sipTrunkPools[0].id : '');
      if (!effectiveSipTrunkPoolId) {
        setDialError(t('common:telephonyFab.dial.sipTrunkPoolRequired', 'Selecione um pool de saída.'));
        return;
      }
      payload.sipTrunkPoolId = effectiveSipTrunkPoolId;
    } else if (selectedMode === 'ai') {
      if (!selectedProviderId) {
        setDialError(t('common:telephonyFab.launcher.providerRequired', 'Selecione um provedor.'));
        return;
      }
      if (!selectedAgentId) {
        setDialError(t('common:telephonyFab.launcher.agentRequired', 'Selecione um agente.'));
        return;
      }
      payload.aiAgentProviderId = selectedProviderId;
      payload.agentId = selectedAgentId;
    } else {
      const sipTrunkPools = bootstrapData?.resources?.sipTrunkPools || [];
      const effectiveSipTrunkPoolId = selectedSipTrunkPoolId || (sipTrunkPools.length === 1 ? sipTrunkPools[0].id : '');
      if (!effectiveSipTrunkPoolId) {
        setDialError(t('common:telephonyFab.dial.sipTrunkPoolRequired', 'Selecione um pool de saída.'));
        return;
      }
      if (reverseIvrNewAudioEnabled) {
        const hasUploadedAudioSelected = Boolean(
          reverseIvrUploadedAudioId
          && selectedReverseIvrAudioId
          && selectedReverseIvrAudioId === reverseIvrUploadedAudioId,
        );
        if (!hasUploadedAudioSelected) {
          setDialError(
            t(
              'common:telephonyFab.launcher.reverseIvrAudioNewRequired',
              'Com "Novo" ativado, envie e selecione um novo áudio antes de iniciar a ligação.',
            ),
          );
          return;
        }
      }
      if (!selectedReverseIvrAudioId) {
        setDialError(t('common:telephonyFab.launcher.reverseIvrAudioRequired', 'Selecione um áudio de URA Reversa.'));
        return;
      }
      payload.sipTrunkPoolId = effectiveSipTrunkPoolId;
      payload.reverseIvrAudioFileId = selectedReverseIvrAudioId;
      if (Object.keys(reverseIvrDtmfMapPayload).length > 0) {
        payload.reverseIvrDtmfMap = reverseIvrDtmfMapPayload;
      }
    }

    setDialSubmitting(true);
    setDialError(null);
    setDialPreCallError(null);
    try {
      const result = await onManualLaunch(payload);
      if (!result.callId) {
        setDialError(t('common:telephonyFab.dial.missingCallId', 'A ligação foi iniciada, mas o identificador da chamada não retornou.'));
        return;
      }
      setDialOpen(false);
      setDialValue('');
    } catch (error: unknown) {
      const preCallError = parseManualCallLaunchPreCallError(error);
      if (preCallError) {
        setDialPreCallError(preCallError);
        console.info('[GlobalTelephonyFab] manual pre-call blocked launch', {
          errorCode: preCallError.errorCode,
          reasonCode: preCallError.reasonCode,
          failureKind: preCallError.failureKind,
          executionId: preCallError.executionId,
        });
      }
      const reasonCode = getApiErrorField(error, 'reason_code') ?? getApiErrorField(error, 'reason');
      setDialError(
        preCallError?.errorCode === 'pre_call_skipped'
          ? t(
            'common:telephonyFab.launcher.preCallSkipped',
            'O script pré-ligação não autorizou esta chamada. Nenhuma ligação foi criada.',
          )
          : preCallError?.errorCode === 'pre_call_retry_required'
            ? t(
              'common:telephonyFab.launcher.preCallRetryRequired',
              'Não foi possível obter o contexto pré-ligação agora. Tente novamente em instantes.',
            )
            : reasonCode
          ? resolveModeUnavailableMessage(String(reasonCode))
          : getApiErrorMessage(
            error,
            t('common:telephonyFab.dial.error', 'Não foi possível iniciar a ligação.'),
          ),
      );
    } finally {
      setDialSubmitting(false);
    }
  };

  const modeLabel = (mode: LaunchMode): string => {
    if (mode === 'telephony_extension') {
      return t('common:telephonyFab.launcher.mode.telephonyExtension', 'Ramal');
    }
    if (mode === 'reverse_ivr') {
      return t('common:telephonyFab.launcher.mode.reverseIvr', 'URA Reversa');
    }
    return t('common:telephonyFab.launcher.mode.ai', 'Agente de IA');
  };

  if (!visible) {
    return null;
  }

  return (
    <>
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          position: 'fixed',
          left: position.x,
          top: position.y,
          zIndex: (theme) => theme.zIndex.modal - 1,
          cursor: dragStateRef.current.dragging ? 'grabbing' : 'grab',
          userSelect: 'none',
        }}
      >
        <Tooltip title={tooltipLabel} arrow>
          <span>
            <Badge
              color="error"
              variant={hasIncomingAlert ? 'dot' : 'standard'}
              overlap="circular"
              invisible={!hasIncomingAlert}
            >
              <Fab
                color={hasIncomingAlert ? 'secondary' : 'primary'}
                disabled={isFabDisabled}
                onClick={handleFabClick}
                sx={{
                  position: 'relative',
                  overflow: 'visible',
                  bgcolor: isFabDisabled ? 'grey.500' : undefined,
                  '&.Mui-disabled': {
                    bgcolor: 'grey.500',
                    color: 'grey.100',
                  },
                  animation: hasIncomingAlert
                    ? `${ringingPulse} 780ms cubic-bezier(0.22, 1, 0.36, 1) infinite, ${ringingGlow} 780ms ease-in-out infinite`
                    : 'none',
                  transformOrigin: 'center',
                  '&::before': shouldAnimateInProgress ? {
                    content: '""',
                    position: 'absolute',
                    inset: -7,
                    borderRadius: '50%',
                    border: '2px solid',
                    borderColor: 'primary.main',
                    animation: `${inProgressOuterHalo} 1.15s ease-in-out infinite`,
                    pointerEvents: 'none',
                  } : undefined,
                }}
              >
                {hasIncomingAlert ? <PhoneCallback /> : hasInProgressCall ? <PhoneInTalk /> : <AddIcCall />}
              </Fab>
            </Badge>
          </span>
        </Tooltip>
      </Box>

      <Dialog
        open={dialOpen}
        onClose={() => {
          if (dialSubmitting || reverseIvrAudioUploading) return;
          setDialOpen(false);
          setDialError(null);
          setDialPreCallError(null);
          setDialModeAlert(null);
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            width: { xs: 'calc(100vw - 24px)', md: '920px' },
            maxWidth: '920px',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcCall color="primary" />
            {t('common:telephonyFab.dial.title', 'Iniciar ligação')}
          </Box>
          <FormControl
            size="small"
            sx={{ minWidth: { xs: 150, sm: 220 }, maxWidth: { xs: '56%', sm: 280 } }}
            disabled={
              bootstrapLoading
              || dialSubmitting
              || reverseIvrAudioUploading
              || enabledModes.length <= 1
              || !hasTenantConcurrencySlot
            }
          >
            <InputLabel id="telephony-fab-mode-label">
              {t('common:telephonyFab.launcher.modeLabel', 'Modalidade')}
            </InputLabel>
            <Select
              labelId="telephony-fab-mode-label"
              value={selectedMode}
              label={t('common:telephonyFab.launcher.modeLabel', 'Modalidade')}
              onChange={(event) => setSelectedMode(String(event.target.value) as LaunchMode)}
            >
              <MenuItem value="" disabled>
                {t('common:telephonyFab.launcher.modePlaceholder', 'Selecione')}
              </MenuItem>
              {enabledModes.map((mode) => (
                <MenuItem key={mode} value={mode}>
                  {modeLabel(mode)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, pt: '12px !important' }}>
          {bootstrapLoading && (
            <Alert
              severity="info"
              icon={<CircularProgress color="inherit" size={18} />}
              sx={{ alignItems: 'center' }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  {t('common:telephonyFab.dial.loadingModes', 'Carregando modalidades de ligação...')}
                </Typography>
                <Typography variant="caption">
                  {t(
                    'common:telephonyFab.dial.loadingModesHint',
                    'Aguarde o carregamento para preencher os campos da ligação.',
                  )}
                </Typography>
              </Box>
            </Alert>
          )}

          {dialModeAlert && (
            <Alert severity="warning">
              {dialModeAlert}
            </Alert>
          )}

          {hasInProgressCall && (
            <Alert
              severity="warning"
              action={onInProgressClick ? (
                <Button color="inherit" size="small" onClick={onInProgressClick}>
                  {t('common:telephonyFab.dial.openInProgress', 'Abrir ligação')}
                </Button>
              ) : undefined}
            >
              {t(
                'common:telephonyFab.dial.extensionBusy',
                'Seu ramal está ocupado em outra ligação. Abra a chamada em andamento para continuar.',
              )}
            </Alert>
          )}

          {!bootstrapLoading && hasTenantConcurrencySlot && enabledModes.length > 0 && (
            <Stack spacing={2.25}>
              <PhoneNumberField
                label={t('common:telephonyFab.dial.destination', 'Número de destino')}
                placeholder={t('common:telephonyFab.dial.destinationPlaceholder', 'Digite o número')}
                value={dialValue || undefined}
                onChange={(value) => setDialValue(value || '')}
                disabled={dialSubmitting}
                autoFocus
              />

              {selectedMode === 'telephony_extension' && (
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    disabled
                    label={t('common:telephonyFab.dial.endpoint', 'Ramal de origem')}
                    value={outboundEndpointLabel || t('common:telephonyFab.noEndpoint', 'Sem ramal configurado')}
                  />
                  <FormControl fullWidth disabled={dialSubmitting}>
                    <InputLabel id="telephony-fab-sip-trunk-pool-label">
                      {t('common:telephonyFab.dial.sipTrunkPool', 'Pool de saída')}
                    </InputLabel>
                    <Select
                      labelId="telephony-fab-sip-trunk-pool-label"
                      label={t('common:telephonyFab.dial.sipTrunkPool', 'Pool de saída')}
                      value={selectedSipTrunkPoolId}
                      onChange={(event) => setSelectedSipTrunkPoolId(String(event.target.value))}
                    >
                      {(bootstrapData?.resources?.sipTrunkPools || []).map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              )}

              {selectedMode === 'ai' && (
                <Stack spacing={2}>
                  <FormControl fullWidth disabled={dialSubmitting}>
                    <InputLabel id="telephony-fab-provider-label">
                      {t('common:telephonyFab.launcher.ai_agent_provider', 'Provedor')}
                    </InputLabel>
                    <Select
                      labelId="telephony-fab-provider-label"
                      label={t('common:telephonyFab.launcher.ai_agent_provider', 'Provedor')}
                      value={selectedProviderId}
                      onChange={(event) => {
                        setSelectedProviderId(String(event.target.value));
                        setSelectedAgentId('');
                        setSelectedPreCallScriptId('');
                        setDialPreCallError(null);
                        setSelectedAppointmentCalendarId('');
                      }}
                    >
                      {(bootstrapData?.resources?.providers || []).map((provider) => (
                        <MenuItem key={provider.id} value={provider.id}>
                          {provider.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Autocomplete<AgentOption, false, false, false>
                    fullWidth
                    disabled={dialSubmitting || !selectedProviderId}
                    options={providerAgents}
                    value={selectedAgent}
                    onChange={(_, nextAgent) => {
                      setSelectedAgentId(nextAgent?.id || '');
                      setSelectedPreCallScriptId('');
                      setDialPreCallError(null);
                      setSelectedAppointmentCalendarId('');
                    }}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    noOptionsText={t(
                      'common:telephonyFab.launcher.noAgents',
                      'Nenhum agente disponível para este provedor.',
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('common:telephonyFab.launcher.agent', 'Agente')}
                      />
                    )}
                  />

                  {isAppointmentCalendarRequired && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarMonth color="primary" fontSize="small" />
                          <Typography variant="subtitle2">
                            {t('common:telephonyFab.launcher.appointmentCalendarTitle')}
                          </Typography>
                        </Box>
                        {appointmentCalendarsError ? (
                          <Alert severity="error">{appointmentCalendarsError}</Alert>
                        ) : appointmentCalendarsLoading ? (
                          <Typography variant="body2" color="text.secondary">
                            {t('common:telephonyFab.launcher.appointmentCalendarLoading')}
                          </Typography>
                        ) : appointmentCalendars.length === 0 ? (
                          <Alert severity="warning">
                            {t('common:telephonyFab.launcher.appointmentCalendarEmpty')}
                          </Alert>
                        ) : (
                          <Autocomplete<AppointmentCalendar, false, false, false>
                            fullWidth
                            options={appointmentCalendars}
                            value={selectedAppointmentCalendar}
                            disabled={dialSubmitting || appointmentCalendars.length === 1}
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            onChange={(_, nextCalendar) => setSelectedAppointmentCalendarId(nextCalendar?.id || '')}
                            noOptionsText={t('common:telephonyFab.launcher.appointmentCalendarEmpty')}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                required
                                label={t('common:telephonyFab.launcher.appointmentCalendar')}
                                helperText={
                                  appointmentCalendars.length === 1
                                    ? t('common:telephonyFab.launcher.appointmentCalendarAutoSelected')
                                    : t('common:telephonyFab.launcher.appointmentCalendarHelp')
                                }
                              />
                            )}
                          />
                        )}
                      </Stack>
                    </Paper>
                  )}

                  {hasAgentDynamicVariables && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Stack spacing={1.5}>
                        <Box>
                          <Typography variant="subtitle2">
                            {t('common:telephonyFab.launcher.aiDynamicVariablesTitle', 'Variáveis dinâmicas do agente')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t(
                              'common:telephonyFab.launcher.aiDynamicVariablesHint',
                              'Esses valores são enviados junto com a ligação manual.',
                            )}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'grid',
                            gap: 1.5,
                            gridTemplateColumns: {
                              xs: '1fr',
                              md: 'repeat(2, minmax(0, 1fr))',
                            },
                          }}
                        >
                          {selectedAgentVariableEntries.map(([key, value]) => (
                            <TextField
                              key={key}
                              size="small"
                              fullWidth
                              label={key}
                              value={value}
                              onChange={(event) => {
                                setAgentVariableValues((prev) => ({
                                  ...prev,
                                  [key]: event.target.value,
                                }));
                              }}
                              disabled={dialSubmitting}
                            />
                          ))}
                        </Box>
                      </Stack>
                    </Paper>
                  )}
                </Stack>
              )}

              {selectedMode === 'reverse_ivr' && (
                <Stack spacing={2}>
                  <FormControl fullWidth disabled={dialSubmitting}>
                    <InputLabel id="telephony-fab-reverse-sip-trunk-pool-label">
                      {t('common:telephonyFab.dial.sipTrunkPool', 'Pool de saída')}
                    </InputLabel>
                    <Select
                      labelId="telephony-fab-reverse-sip-trunk-pool-label"
                      label={t('common:telephonyFab.dial.sipTrunkPool', 'Pool de saída')}
                      value={selectedSipTrunkPoolId}
                      onChange={(event) => setSelectedSipTrunkPoolId(String(event.target.value))}
                    >
                      {(bootstrapData?.resources?.sipTrunkPools || []).map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box
                    sx={{
                      display: 'grid',
                      gap: 1.5,
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: 'minmax(0, 1fr) auto',
                      },
                      alignItems: 'center',
                    }}
                  >
                    <Autocomplete
                      options={reverseIvrAudioOptions}
                      value={selectedReverseIvrAudioOption}
                      disabled={dialSubmitting || reverseIvrAudioUploading || reverseIvrNewAudioEnabled}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      onChange={(_, nextValue) => setSelectedReverseIvrAudioId(nextValue?.id || '')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('common:telephonyFab.launcher.reverseIvrAudio', 'Áudio URA Reversa')}
                          placeholder={t('common:telephonyFab.launcher.reverseIvrAudioNone', 'Nenhum áudio selecionado')}
                        />
                      )}
                    />
                    <FormControlLabel
                      sx={{
                        m: 0,
                        justifySelf: { xs: 'flex-start', md: 'flex-end' },
                      }}
                      control={(
                        <Switch
                          size="small"
                          checked={reverseIvrNewAudioEnabled}
                          onChange={(event) => {
                            const enableNewAudio = event.target.checked;
                            setReverseIvrNewAudioEnabled(enableNewAudio);
                            if (enableNewAudio) {
                              setSelectedReverseIvrAudioId('');
                              setReverseIvrAudioLookupLoading(false);
                            }
                          }}
                          disabled={dialSubmitting || reverseIvrAudioUploading}
                        />
                      )}
                      label={t('common:telephonyFab.launcher.reverseIvrAudioNewToggle', 'Novo')}
                    />
                  </Box>

                  {selectedReverseIvrAudioId && !showUploadedReverseIvrWaveform && selectedReverseIvrAudioPreviewUrl && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Stack spacing={1.5}>
                        <Typography variant="subtitle2">
                          {t('common:telephonyFab.launcher.reverseIvrAudioPreview', 'Preview do áudio selecionado')}
                        </Typography>
                        <WaveformPlayer
                          url={selectedReverseIvrAudioPreviewUrl}
                          displayName={selectedReverseIvrAudioOption?.name || undefined}
                          downloadFilename={selectedReverseIvrAudioOption?.name || undefined}
                        />
                      </Stack>
                    </Paper>
                  )}

                  {selectedReverseIvrAudioId && reverseIvrAudioLookupLoading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption" color="text.secondary">
                        {t('common:telephonyFab.launcher.reverseIvrAudioPreviewLoading', 'Carregando preview do áudio...')}
                      </Typography>
                    </Box>
                  )}

                  {selectedReverseIvrAudioId
                    && !reverseIvrAudioLookupLoading
                    && !selectedReverseIvrAudioPreviewUrl
                    && !showUploadedReverseIvrWaveform && (
                    <Alert severity="warning">
                      {t(
                        'common:telephonyFab.launcher.reverseIvrAudioPreviewUnavailable',
                        'Não foi possível carregar o preview desse áudio no momento.',
                      )}
                    </Alert>
                  )}

                  {reverseIvrNewAudioEnabled && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Stack spacing={1.5}>
                        <Box>
                          <Typography variant="subtitle2">
                            {t('common:telephonyFab.launcher.reverseIvrAudioUploadTitle', 'Criar novo áudio')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t(
                              'common:telephonyFab.launcher.reverseIvrAudioUploadHint',
                              'Envie um arquivo de áudio para criar e selecionar automaticamente na URA Reversa.',
                            )}
                          </Typography>
                        </Box>
                        <WaveformPlayerField
                          url={showUploadedReverseIvrWaveform ? (selectedReverseIvrAudioPreviewUrl || undefined) : undefined}
                          displayName={showUploadedReverseIvrWaveform
                            ? (selectedReverseIvrAudioOption?.name || reverseIvrUploadedFilename || undefined)
                            : undefined}
                          downloadFilename={showUploadedReverseIvrWaveform
                            ? (selectedReverseIvrAudioOption?.name || reverseIvrUploadedFilename || undefined)
                            : undefined}
                          uploadEnabled
                          disabled={dialSubmitting}
                          isUploading={reverseIvrAudioUploading}
                          uploadProgress={reverseIvrAudioUploadProgress}
                          constraints={reverseIvrAudioFieldConstraints}
                          showConstraints={Boolean(ivrMaxAudioSeconds || ivrMaxAudioFileSizeBytes)}
                          onSelectFile={handleReverseIvrAudioUpload}
                          onRemove={showUploadedReverseIvrWaveform ? handleClearReverseIvrUploadSelection : undefined}
                        />
                        {reverseIvrAudioUploadError && (
                          <Alert severity="error">{reverseIvrAudioUploadError}</Alert>
                        )}
                      </Stack>
                    </Paper>
                  )}

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Box>
                          <Typography variant="subtitle2">
                            {t('common:telephonyFab.launcher.reverseIvrDtmfTitle', 'Mapeamento DTMF')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t(
                              'common:telephonyFab.launcher.reverseIvrDtmfHint',
                              'Opcional: configure o resultado da chamada para cada dígito pressionado.',
                            )}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Add fontSize="small" />}
                          onClick={() => setReverseIvrDtmfRules((prev) => [...prev, createReverseIvrDtmfRule()])}
                          disabled={dialSubmitting || reverseIvrAudioUploading || reverseIvrDtmfRules.length >= 10}
                        >
                          {t('common:telephonyFab.launcher.reverseIvrDtmfAddRule', 'Adicionar regra')}
                        </Button>
                      </Box>

                      {reverseIvrDtmfRules.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          {t(
                            'common:telephonyFab.launcher.reverseIvrDtmfEmpty',
                            'Nenhuma regra adicionada. Sem mapeamento explícito, os dígitos serão tratados como não mapeados.',
                          )}
                        </Typography>
                      ) : (
                        <Stack spacing={1}>
                          {reverseIvrDtmfRules.map((rule) => (
                            <Box
                              key={rule.id}
                              sx={{
                                display: 'grid',
                                gap: 1,
                                gridTemplateColumns: {
                                  xs: '1fr',
                                  md: '120px 190px minmax(0, 1fr) auto',
                                },
                                alignItems: 'center',
                              }}
                            >
                              <FormControl size="small">
                                <InputLabel id={`telephony-fab-ivr-digit-label-${rule.id}`}>
                                  {t('common:telephonyFab.launcher.reverseIvrDtmfDigit', 'Dígito')}
                                </InputLabel>
                                <Select
                                  labelId={`telephony-fab-ivr-digit-label-${rule.id}`}
                                  value={rule.digit}
                                  label={t('common:telephonyFab.launcher.reverseIvrDtmfDigit', 'Dígito')}
                                  disabled={dialSubmitting || reverseIvrAudioUploading}
                                  onChange={(event) => {
                                    const nextDigit = String(event.target.value);
                                    setReverseIvrDtmfRules((prev) => prev.map((entry) => (
                                      entry.id === rule.id ? { ...entry, digit: nextDigit } : entry
                                    )));
                                  }}
                                >
                                  {Array.from({ length: 10 }).map((_, digit) => (
                                    <MenuItem key={digit} value={String(digit)}>
                                      {digit}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>

                              <FormControl size="small">
                                <InputLabel id={`telephony-fab-ivr-status-label-${rule.id}`}>
                                  {t('common:telephonyFab.launcher.reverseIvrDtmfStatus', 'Status')}
                                </InputLabel>
                                <Select
                                  labelId={`telephony-fab-ivr-status-label-${rule.id}`}
                                  value={rule.status}
                                  label={t('common:telephonyFab.launcher.reverseIvrDtmfStatus', 'Status')}
                                  disabled={dialSubmitting || reverseIvrAudioUploading}
                                  onChange={(event) => {
                                    const nextStatus = String(event.target.value) as ReverseIvrDtmfStatus;
                                    setReverseIvrDtmfRules((prev) => prev.map((entry) => (
                                      entry.id === rule.id ? { ...entry, status: nextStatus } : entry
                                    )));
                                  }}
                                >
                                  {REVERSE_IVR_DTMF_STATUSES.map((status) => (
                                    <MenuItem key={status} value={status}>
                                      {t(
                                        `common:telephonyFab.launcher.reverseIvrDtmfStatusValues.${status}`,
                                        status,
                                      )}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>

                              <TextField
                                size="small"
                                label={t('common:telephonyFab.launcher.reverseIvrDtmfLabel', 'Rótulo')}
                                value={rule.label}
                                disabled={dialSubmitting || reverseIvrAudioUploading}
                                onChange={(event) => {
                                  const nextLabel = event.target.value;
                                  setReverseIvrDtmfRules((prev) => prev.map((entry) => (
                                    entry.id === rule.id ? { ...entry, label: nextLabel } : entry
                                  )));
                                }}
                              />

                              <IconButton
                                color="error"
                                aria-label={t('common:telephonyFab.launcher.reverseIvrDtmfRemoveRule', 'Remover regra')}
                                disabled={dialSubmitting || reverseIvrAudioUploading}
                                onClick={() => {
                                  setReverseIvrDtmfRules((prev) => prev.filter((entry) => entry.id !== rule.id));
                                }}
                              >
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Box>
                          ))}
                        </Stack>
                      )}

                      {reverseIvrDtmfValidationError && (
                        <Alert severity="warning">
                          {reverseIvrDtmfValidationError === 'duplicate_digit'
                            ? t(
                              'common:telephonyFab.launcher.reverseIvrDtmfDuplicateDigit',
                              'Não é permitido repetir o mesmo dígito no mapeamento DTMF.',
                            )
                            : t(
                              'common:telephonyFab.launcher.reverseIvrDtmfMissingDigit',
                              'Selecione um dígito para todas as regras de mapeamento DTMF.',
                            )}
                        </Alert>
                      )}
                    </Stack>
                  </Paper>

                  {ivrMaxAudioSeconds ? (
                    <Alert severity="info">
                      {t('common:telephonyFab.launcher.reverseIvrAudioLimit', {
                        defaultValue: 'Limite máximo de áudio: {{seconds}}s',
                        seconds: ivrMaxAudioSeconds,
                      })}
                    </Alert>
                  ) : null}
                </Stack>
              )}

              {selectedMode === 'ai' && (preCallScripts.length > 0 || isPreCallScriptRequired) && (
                preCallScripts.length === 0 ? (
                  <Alert severity="warning">
                    {t('common:telephonyFab.launcher.preCallScriptRequiredUnavailable')}
                  </Alert>
                ) : (
                <Autocomplete<ScriptOption, false, false, false>
                  options={preCallScripts}
                  value={selectedPreCallScriptOption}
                  disabled={dialSubmitting}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={(_, nextValue) => {
                    setSelectedPreCallScriptId(nextValue?.id || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required={isPreCallScriptRequired}
                      label={t(
                        'common:telephonyFab.launcher.preCallScript',
                        'Script pré-ligação',
                      )}
                      placeholder={t(
                        'common:telephonyFab.launcher.preCallScriptPlaceholder',
                        'Selecione o gate ou enriquecimento',
                      )}
                      helperText={t(
                        'common:telephonyFab.launcher.preCallScriptHelp',
                        'Executado antes de criar e iniciar a chamada.',
                      )}
                    />
                  )}
                />
                )
              )}

              {canUseScripts && callScripts.length > 0 && (
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={callScripts}
                  value={selectedScriptOptions}
                  disabled={dialSubmitting}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={(_, nextValue) => {
                    setSelectedScriptIds(nextValue.map((item) => item.id));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('common:telephonyFab.launcher.scripts', 'Scripts')}
                      placeholder={t('common:telephonyFab.launcher.scriptsPlaceholder', 'Automação')}
                    />
                  )}
                />
              )}

              {isTransferTargetRequired && (
                <Autocomplete
                  options={transferTargetOptions}
                  value={selectedTransferTargetOption}
                  disabled={dialSubmitting}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  onChange={(_, nextValue) => setSelectedTransferTarget(nextValue?.value || '')}
                  noOptionsText={t('common:telephonyFab.launcher.transferTargetNone', 'Nenhum')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label={t('common:telephonyFab.launcher.transferTarget', 'Destino de transferência')}
                      placeholder={t('common:telephonyFab.launcher.transferTargetNone', 'Nenhum')}
                    />
                  )}
                />
              )}
            </Stack>
          )}

          {dialError && (
            <Alert severity={dialPreCallError?.failureKind === 'business_retry' ? 'warning' : 'error'}>
              <Stack spacing={0.75}>
                {dialPreCallError && (
                  <Typography variant="subtitle2">
                    {t(`common:telephonyFab.launcher.preCallFailureKinds.${dialPreCallError.failureKind}`)}
                  </Typography>
                )}
                <Typography variant="body2">{dialError}</Typography>
                {dialPreCallError && !dialPreCallError.reasonCode && (
                  <Typography variant="body2">
                    {t('common:telephonyFab.launcher.preCallReasonMissing')}
                  </Typography>
                )}
                {dialPreCallError?.reasonCode && (
                  <Typography variant="caption" sx={{ userSelect: 'text' }}>
                    {t('common:telephonyFab.launcher.preCallReasonLabel')}: {dialPreCallError.reasonCode}
                  </Typography>
                )}
                {dialPreCallError?.retryAfterSeconds !== null && dialPreCallError?.retryAfterSeconds !== undefined && (
                  <Typography variant="caption">
                    {t('common:telephonyFab.launcher.preCallRetryAfter', {
                      seconds: dialPreCallError.retryAfterSeconds,
                    })}
                  </Typography>
                )}
                {dialPreCallError?.executionId && (
                  <Typography variant="caption" sx={{ userSelect: 'text' }}>
                    {t('common:telephonyFab.launcher.preCallExecutionLabel')}: {dialPreCallError.executionId}
                  </Typography>
                )}
                {dialPreCallError && Object.entries(dialPreCallError.details).map(([key, value]) => (
                  <Typography key={key} variant="caption" sx={{ userSelect: 'text' }}>
                    {key}: {value}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialOpen(false);
              setDialError(null);
              setDialPreCallError(null);
              setDialModeAlert(null);
            }}
            disabled={dialSubmitting || reverseIvrAudioUploading}
          >
            {t('common:telephonyFab.dial.cancel', 'Cancelar')}
          </Button>
          <Button
            onClick={handleDialSubmit}
            variant="contained"
            disabled={dialSubmitting || reverseIvrAudioUploading || !isDialFormReady}
            sx={{
              minWidth: 108,
              '&.Mui-disabled': {
                opacity: 1,
                bgcolor: (theme) => theme.palette.action.disabledBackground,
                color: (theme) => theme.palette.text.secondary,
              },
            }}
          >
            {dialSubmitting
              ? t('common:telephonyFab.dial.starting', 'Iniciando...')
              : t('common:telephonyFab.dial.start', 'Ligar')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
